import { useEffect, useMemo, useState } from 'react';
import { consentCopyLanguages, consentTextFor } from '@/lib/consentText';
import { ConsentCopies, documentLanguage } from '@/screens/parts/ConsentCopies';
import { boundarySnapshot } from '@/lib/mapSnapshot';
import { relationName } from '@/lib/relations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FilePen, FileText, Link2, Mail, MessageCircle, Plus, Printer, Send, ShieldCheck, Undo2 } from 'lucide-react';
import { Field, Select, StatusPill, formatDate, formatDateTime, todayIso } from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { ValidityBadge } from '@/components/ValidityBadge';
import { callRpc, listRows } from '@/api/resources';
import { signedUrlsFor, uploadDocument } from '@/api/documents';
import { plotOptions, referenceValues } from '@/api/lookups';
import { otpChannels } from '@/api/otp';
import { issueConsentReviewLink, sendConfirmationNotice, sendRecordDocument } from '@/api/documentSend';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useRows } from '@/hooks/useResource';
import { useLetterhead } from '@/hooks/useLetterhead';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { languageName } from '@/lib/languages';
import { EXCISION_REASONS, PLOT_SCOPES, scopeLabel } from '@/lib/plotScopes';
import { PLOT_CONSENT_TYPES, buildPlotConsentHtml, plotConsentKind, sha256Hex, type PlotConsentData, type PlotConsentKind } from '@/lib/plotConsentDocument';
import { htmlToPdf } from '@/lib/pdf';
import { ConsentCapture, type OtpSentVia } from '@/screens/parts/ConsentCapture';
import { PaperConsent } from '@/screens/parts/PaperConsent';
import { WITHDRAWAL_REASONS } from '@/screens/parts/WithdrawalRegister';
import type { ConsentRow } from '@/types/records';

interface LinkedDocument { link_id: string; link_type: string; linked_at: string; document_id: string; original_filename: string | null; mime_type: string | null; sha256_hash?: string | null }
interface FarmerFacts {
  id: string; party_id: string; full_name: string; farmer_code: string | null; relation_type?: string | null; father_or_spouse_name: string | null; gender: string | null;
  mobile: string | null; communication_address: string | null; pin_code: string | null; village_name: string | null; block_name: string | null;
  district_name: string | null; state_name: string | null; photo_document_id: string | null; language_order: string[] | null; preferred_language: string | null;
}
interface PlotFacts {
  id: string; plot_code: string; plot_no: string | null; plot_name: string | null; farmer_id: string; village_name: string | null;
  block_name: string | null; district_name: string | null; state_name: string | null; declared_area_ha: number | null; mapped_area_ha: number | null;
  land_tenure_type_id: string | null;
}
interface ScopeFacts {
  scope_code: string; certificate_no: string | null; reference_no: string | null; valid_to: string | null; status: string;
  excision_declaration?: string | null; excised_area_ha?: number | null; excision_reason?: string | null; excision_description?: string | null;
}

/** How each plot consent is named on screen, and in its file names. */
export const PLOT_CONSENT_NAMES: Record<PlotConsentKind, { title: string; file: string }> = {
  programme: { title: 'Plot consent', file: 'plot-consent' },
  sale: { title: 'Sale consent', file: 'sale-consent' },
  family: { title: 'Family members\' consent', file: 'family-consent' },
  sharecrop: { title: 'Sharecropping consent', file: 'sharecrop-consent' },
  representative: { title: 'Representative consent', file: 'representative-consent' },
  dataProcessing: { title: 'Data processing consent', file: 'data-consent' },
  trading: { title: 'Trading consent', file: 'trading-consent' },
  documentShare: { title: 'Document sharing consent', file: 'document-share-consent' },
  payment: { title: 'Payment consent', file: 'payment-consent' },
  membership: { title: 'Membership consent', file: 'membership-consent' },
  portalAccess: { title: 'Portal access consent', file: 'portal-consent' },
  harvestRequest: { title: 'Harvest consent', file: 'harvest-consent' },
  eudrClaim: { title: 'EUDR / FSC claim consent', file: 'claim-consent' },
  withdrawal: { title: 'Withdrawal', file: 'withdrawal' },
  amendment: { title: 'Consent amendment', file: 'consent-amendment' },
};

const ROLE_WORD: Record<string, string> = { family_member: 'Family member', co_owner: 'Co-owner', landowner: 'Landowner', sharecropper: 'Sharecropper' };

const CONSENT_STATE: Record<string, string> = { pending: 'Waiting for the farmer', generated: 'Waiting for the farmer', acknowledged: 'Consent given', revoked: 'Withdrawn', expired: 'Expired' };

/**
 * The plot's mapped boundary, its outer ring as [longitude, latitude] pairs - for the consent's
 * geolocation (owner, 2026-09-28: "need to include GPS coordinates of the plot?"). Null when the
 * plot is not mapped yet.
 */
async function boundaryOf(plotId: string | null | undefined): Promise<Array<[number, number]> | null> {
  if (!plotId) return null;
  try {
    const result = await callRpc<{ geometry?: { type: string; coordinates: unknown } } | null>('fn_plot_geojson', { p_plot_id: plotId });
    const geometry = result?.geometry;
    if (!geometry) return null;
    if (geometry.type === 'Polygon') return (geometry.coordinates as Array<Array<[number, number]>>)[0] ?? null;
    if (geometry.type === 'MultiPolygon') return (geometry.coordinates as Array<Array<Array<[number, number]>>>)[0]?.[0] ?? null;
    return null;
  } catch {
    return null;
  }
}

/** A stored file as a data URI, so the PDF maker can draw it (a signed link is another origin). */
async function dataUriOf(documentId: string | null | undefined): Promise<string | null> {
  if (!documentId) return null;
  try {
    const url = (await signedUrlsFor([documentId]))[documentId];
    if (!url) return null;
    const blob = await (await fetch(url)).blob();
    return await new Promise((resolve) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.readAsDataURL(blob); });
  } catch {
    return null;
  }
}

/**
 * A plot's consents: the one in force, the history, and "New consent" (owner, 2026-09-27: "in plot,
 * we need to have option to send consent and consent history with validity"). Used on the plot,
 * on the farmer (every plot's) and on the Plot consents page.
 */
export function PlotConsentList({ plotId, farmerPartyId, linkOnly = false }: {
  plotId?: string; farmerPartyId?: string;
  /** Consents are worked on the Plot consents page (owner, 2026-09-28): the list links there. */
  linkOnly?: boolean;
}) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const mayIssue = can(profile, 'party.consent', 'create');
  const consents = useRows<ConsentRow>('party_consents', {
    filters: { plot_id: plotId, party_id: farmerPartyId },
    orderBy: { column: 'created_at', ascending: false },
  });
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Every consent of the plot: programme, sale, and the family's or sharecropping one.
  const rows = (consents.data ?? []).filter((row) => row.plot_id && (PLOT_CONSENT_TYPES as readonly string[]).includes(row.consent_type));
  return (
    <section className="child-records">
      <div className="section-heading">
        <h3>Consents <span className="muted">({rows.length})</span></h3>
        {mayIssue && !linkOnly && <button onClick={() => setAdding(true)} type="button"><Plus size={15} /> New consent</button>}
      </div>
      {rows.length === 0 && <p className="muted child-records-intro">No consent has been taken for {plotId ? 'this plot' : 'any of these plots'} yet.</p>}
      <ul className="child-record-list">
        {rows.map((row) => (
          <li key={row.id}>
            <button className="child-record-text link-button" onClick={() => (linkOnly ? navigate(`/plot-consents?consent=${row.id}`) : setOpen(row.id))} type="button">
              <strong>{[row.plot_code, row.plot_no].filter(Boolean).join(' · ')} · {row.consent_type === 'plot_enrolment'
                ? (row.scope_codes ?? []).map(scopeLabel).join(', ') || 'Membership' : PLOT_CONSENT_NAMES[plotConsentKind(row.consent_type)].title} <StatusPill value={CONSENT_STATE[row.status] ?? row.status} /></strong>
              <small className="muted">
                {languageName(row.language_code)} · {row.status === 'revoked'
                  ? <>withdrawn {row.revoked_at ? formatDate(row.revoked_at) : ''}</>
                  : row.valid_to ? <>valid to {formatDate(row.valid_to)}</> : 'no validity'}
                {row.acknowledged_at ? ` · given ${formatDateTime(row.acknowledged_at)}` : ''}
                {row.document_sent_at ? ` · PDF sent by ${(row.document_sent_via ?? []).join(', ')}` : ''}
              </small>
            </button>
            {/* A withdrawn consent is not "valid to 2036" (found in the 2026-09-28 UI pass). */}
            {row.valid_to && row.status !== 'revoked' && <ValidityBadge compact from={row.valid_from} to={row.valid_to} />}
          </li>
        ))}
      </ul>
      {adding && <NewPlotConsent onClose={() => setAdding(false)} onCreated={(id) => { setAdding(false); setOpen(id); }} plotId={plotId} />}
      {open && <PlotConsentDrawer consentId={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

/** Issue a plot's consent: the plot, the standards it covers (its scopes in force), the language, ten years. */
export function NewPlotConsent({ plotId: fixedPlot, onClose, onCreated }: { plotId?: string; onClose: () => void; onCreated: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [plotId, setPlotId] = useState(fixedPlot ?? '');
  const [scopes, setScopes] = useState<string[] | null>(null);
  const [language, setLanguage] = useState('');
  const [years, setYears] = useState('10');
  const [saving, setSaving] = useState(false);
  const plots = useQuery({ queryKey: ['picker', 'plots', 'with-farmer'], queryFn: () => plotOptions(), enabled: isSupabaseConfigured && !fixedPlot });
  const plotScopes = useRows<ScopeFacts>('plot_scopes', { filters: { plot_id: plotId } }, Boolean(plotId));
  const plot = useRows<PlotFacts>('farm_plots', { filters: { id: plotId } }, Boolean(plotId));
  const farmer = useRows<FarmerFacts>('farmers', { filters: { id: plot.data?.[0]?.farmer_id } }, Boolean(plot.data?.[0]?.farmer_id));
  const live = (plotScopes.data ?? []).filter((row) => row.status === 'active' && (!row.valid_to || row.valid_to >= todayIso())).map((row) => row.scope_code);
  const chosen = scopes ?? live;
  const f = farmer.data?.[0];
  // Every consent is made in English and one more language (owner, 2026-09-28: "default show 2
  // languages? and generate 2 docs"): the farmer's own language first, which is the one chosen.
  const languages = [...new Set([...(f?.language_order ?? []), f?.preferred_language ?? '', 'hi'].filter((code) => code && code !== 'en')), 'en']
    .map((code) => ({ id: code, label: code === 'en' ? 'English only · 1 document' : `English + ${languageName(code)} · 2 documents` }));
  useEffect(() => { if (!language && f && languages[0]) setLanguage(languages[0].id); }, [language, languages, f]);

  async function create() {
    if (!plotId) { toast.error('Choose the plot.'); return; }
    setSaving(true);
    try {
      const id = await callRpc<string>('fn_issue_plot_consent', {
        p_plot_id: plotId, p_scope_codes: chosen, p_language_code: language || null, p_valid_years: Number(years) || 10,
      });
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
      toast.success('Consent created. Generate the document next.');
      onCreated(id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The consent could not be created.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <RecordDrawer
      footer={<><button className="cancel" onClick={onClose} type="button">Cancel</button>
        <button className="primary" disabled={saving || !plotId} onClick={() => void create()} type="button">{saving ? 'Creating...' : 'Create consent'}</button></>}
      onClose={onClose} open record={null} subtitle="Consent is taken per plot, for the standards the plot is in." title="New plot consent"
    >
      {!fixedPlot && (
        <Field label="Plot" required>
          <Select onChange={(value) => { setPlotId(value); setScopes(null); setLanguage(''); }} options={(plots.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                  placeholder="Select the plot" value={plotId} />
        </Field>
      )}
      {plotId && (
        <Field hint="The plot's scopes in force are switched on. Each becomes a page of obligations in the document." label="Standards this consent covers">
          <ul className="toggle-list">
            {PLOT_SCOPES.map((scope) => {
              const on = chosen.includes(scope.code);
              return (
                <li key={scope.code}>
                  <span>{scope.label}{live.includes(scope.code) ? '' : <small className="muted"> - not in the plot's scopes</small>}</span>
                  <button aria-pressed={on} className="onoff-toggle" type="button"
                          onClick={() => setScopes((now) => {
                            const base = now ?? live;
                            return base.includes(scope.code) ? base.filter((code) => code !== scope.code) : [...base, scope.code];
                          })}>{on ? 'On' : 'Off'}</button>
                </li>
              );
            })}
          </ul>
        </Field>
      )}
      <div className="field-row">
        <Field hint="English, and the farmer's own language: a copy in each." label="Languages">
          <Select onChange={setLanguage} options={languages} value={language} />
        </Field>
        <Field hint="Ten years unless the programme needs otherwise." label="Valid for (years)">
          <input inputMode="numeric" onChange={(event) => setYears(event.target.value.replace(/\D/g, ''))} value={years} />
        </Field>
      </div>
    </RecordDrawer>
  );
}

/**
 * One plot consent, start to finish: generate the PDF and review it, send it by WhatsApp or email
 * (SMS carries only the code) or print it, take the farmer's OTP, and keep the signed copy -
 * stamped "Consent given" with how and when, and the fingerprint of the draft they were sent.
 * The paper route and withdrawal use the flows the Consent screen already has.
 */
export function PlotConsentDrawer({ consentId, onClose }: { consentId: string; onClose: () => void }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayCapture = can(profile, 'party.consent', 'update');
  const consentRows = useRows<ConsentRow>('party_consents', { filters: { id: consentId } });
  const consent = consentRows.data?.[0];
  const plot = useRows<PlotFacts>('farm_plots', { filters: { id: consent?.plot_id ?? undefined } }, Boolean(consent?.plot_id));
  const p = plot.data?.[0];
  const farmer = useRows<FarmerFacts>('farmers', { filters: { party_id: consent?.party_id } }, Boolean(consent?.party_id));
  const f = farmer.data?.[0];
  // The contacts are the party's: the farmer's own record may not repeat them.
  const party = useRows<{ id: string; primary_mobile: string | null; primary_email: string | null }>(
    'parties', { filters: { id: consent?.party_id } }, Boolean(consent?.party_id));
  const scopes = useRows<ScopeFacts>('plot_scopes', { filters: { plot_id: consent?.plot_id ?? undefined } }, Boolean(consent?.plot_id));
  const kyc = useRows<{ kyc_type_label: string | null; masked_document_no: string | null; verification_status: string }>(
    'party_kyc_records', { filters: { party_id: consent?.party_id } }, Boolean(consent?.party_id));
  const species = useRows<{ species_label: string | null; survival_count: number | null; tree_count: number | null }>(
    'plot_species_inventories', { filters: { plot_id: consent?.plot_id ?? undefined } }, Boolean(consent?.plot_id));
  const landDocs = useRows<{ id: string }>('plot_land_documents', { filters: { plot_id: consent?.plot_id ?? undefined } }, Boolean(consent?.plot_id));
  const tenures = useQuery({ queryKey: ['ref', 'land_tenure_type'], queryFn: () => referenceValues('land_tenure_type'), enabled: isSupabaseConfigured });
  const documents = useRows<LinkedDocument>('record_documents', {
    filters: { entity_table: 'party.party_consents', entity_id: consentId }, orderBy: { column: 'linked_at', ascending: false },
  });
  const kind = plotConsentKind(consent?.consent_type);
  const names = PLOT_CONSENT_NAMES[kind];
  // Family and sharecropping: the others who give the consent are the plot's rights holders
  // (migration 750000) - its family members, or its landowner and sharecropper.
  const holders = useRows<{ full_name: string; holder_role: string; relationship_label: string | null; mobile: string | null; share_pct: number | null; status: string }>(
    'plot_rights_holders', { filters: { plot_id: consent?.plot_id ?? undefined } }, Boolean(consent?.plot_id) && (kind === 'family' || kind === 'sharecrop'));
  // A representative consent: the person it appoints (party_representatives.consent_id).
  const appointed = useRows<{ representative_name: string | null; representative_mobile: string | null; relationship_label: string | null;
                              father_or_spouse_name: string | null; relation_type: string | null; photo_document_id: string | null; plot_codes: string | null;
                              can_acknowledge: boolean; can_confirm_sale_quantity: boolean; can_receive_payment_notification: boolean }>(
    'party_representatives', { filters: { consent_id: consentId } }, kind === 'representative');
  // A withdrawal: what it withdraws (withdrawals.consent_id).
  const withdrawalFacts = useRows<{ withdrawal_code: string | null; scope: string; plot_codes: string | null; reason_label: string | null;
                                    reason_text: string | null; effective_date: string | null }>(
    'withdrawals', { filters: { consent_id: consentId } }, kind === 'withdrawal');
  // An amendment: the signed consent it changes; a signed consent: the amendments made to it
  // (migration 20261008100000, owner 2026-10-08: "build amendments as a real feature").
  const original = useRows<ConsentRow>('party_consents', { filters: { id: consent?.amends_consent_id ?? undefined } }, Boolean(consent?.amends_consent_id));
  const amendments = useRows<ConsentRow>('party_consents', { filters: { amends_consent_id: consentId }, orderBy: { column: 'created_at', ascending: true } },
    consent?.status === 'acknowledged');
  const [amending, setAmending] = useState(false);
  const navigate = useNavigate();
  const mayIssue = can(profile, 'party.consent', 'create');
  const arrangements = useRows<{ terms_summary: string | null; status: string }>(
    'plot_tenure_arrangements', { filters: { plot_id: consent?.plot_id ?? undefined } }, Boolean(consent?.plot_id) && kind === 'sharecrop');
  const parties = useMemo(() => (holders.data ?? [])
    .filter((row) => row.status === 'active')
    .filter((row) => (kind === 'family' ? ['family_member', 'co_owner'] : ['landowner', 'sharecropper']).includes(row.holder_role))
    .map((row) => ({ name: row.full_name, relation: row.relationship_label ?? ROLE_WORD[row.holder_role] ?? row.holder_role, mobile: row.mobile, share: row.share_pct })),
  [kind, holders.data]);
  const channels = useQuery({ queryKey: ['otp-channels', consent?.party_id], queryFn: () => otpChannels(consent!.party_id), enabled: Boolean(consent?.party_id) && isSupabaseConfigured });
  const letterhead = useLetterhead(consent?.party_id);

  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sendBy, setSendBy] = useState<Array<'whatsapp' | 'email'>>([]);
  const [capturing, setCapturing] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [paper, setPaper] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const [withdrawalConsent, setWithdrawalConsent] = useState<string | null>(null);

  // One document in both languages (owner, 2026-09-28: "all docs generated as bilingual"): English
  // with the farmer's own language under it. Its file name ends in the farmer's language.
  const lang = consentCopyLanguages(consent?.language_code).at(-1) ?? 'en';
  const languages = [lang];
  const pair = lang === 'en' ? 'en' : `en-${lang}`;
  const latestOf = (lang: string, type: 'generated' | 'primary') => (documents.data ?? [])
    .filter((row) => row.link_type === type && documentLanguage(row.original_filename) === lang && (type === 'primary' || row.mime_type === 'application/pdf'))
    .sort((a, b) => b.linked_at.localeCompare(a.linked_at))[0];
  const drafts = languages.map((code) => latestOf(code, 'generated')).filter((row): row is LinkedDocument => Boolean(row));
  const signedCopies = languages.map((code) => latestOf(code, 'primary')).filter((row): row is LinkedDocument => Boolean(row));
  const draft = drafts[0];
  // The print copy, made with the draft (owner, 2026-09-28: "both digital and print as standard").
  const printCopy = (documents.data ?? []).filter((row) => row.link_type === 'generated' && /-print\.pdf$/.test(row.original_filename ?? ''))
    .sort((a, b) => b.linked_at.localeCompare(a.linked_at))[0];
  const signed = signedCopies[0];
  const waiting = consent ? ['pending', 'generated'].includes(consent.status) : false;
  const docChannels = (channels.data ?? []).filter((row) => row.channel !== 'sms');
  useEffect(() => { setSendBy(docChannels.filter((row) => row.available).map((row) => row.channel as 'whatsapp' | 'email')); },
    [channels.data]); // eslint-disable-line react-hooks/exhaustive-deps -- once the contacts are known
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const scopeRows = useMemo(() => (consent?.scope_codes ?? []).map((code) => {
    const held = (scopes.data ?? []).find((row) => row.scope_code === code);
    return { code, label: scopeLabel(code), certificate: held?.certificate_no ?? held?.reference_no ?? null, validTo: held?.valid_to ?? null };
  }), [consent?.scope_codes, scopes.data]);

  async function documentData(): Promise<PlotConsentData> {
    const area = (value: number | null) => (value == null ? null : `${value.toFixed(3)} ha`);
    const ring = await boundaryOf(consent!.plot_id);
    const repPhoto = kind === 'representative' ? await dataUriOf(appointed.data?.[0]?.photo_document_id) : null;
    return {
      kind,
      parties,
      withdrawal: (() => {
        const row = withdrawalFacts.data?.[0];
        if (kind !== 'withdrawal' || !row) return null;
        return {
          code: row.withdrawal_code, plots: row.scope === 'plots' && row.plot_codes ? row.plot_codes.split(/,\s*/).filter(Boolean) : null,
          reason: [row.reason_label, row.reason_text].filter(Boolean).join(': ') || null, effective: row.effective_date,
        };
      })(),
      representative: (() => {
        const row = appointed.data?.[0];
        if (kind !== 'representative' || !row) return null;
        return {
          name: row.representative_name ?? '', relation: row.relationship_label, mobile: row.representative_mobile,
          fatherOrSpouse: relationName(row.relation_type, row.father_or_spouse_name),
          powers: { acknowledge: row.can_acknowledge, confirmSale: row.can_confirm_sale_quantity, receivePayment: row.can_receive_payment_notification },
          plots: row.plot_codes ? row.plot_codes.split(/,\s*/).filter(Boolean) : null, photoDataUri: repPhoto,
        };
      })(),
      terms: (arrangements.data ?? []).find((row) => row.status === 'active' && row.terms_summary)?.terms_summary ?? null,
      amendment: (() => {
        const o = original.data?.[0];
        if (kind !== 'amendment' || !o) return null;
        return {
          no: consent!.amendment_no ?? 1,
          original: { id: o.id, title: PLOT_CONSENT_NAMES[plotConsentKind(o.consent_type)].title, plot: [o.plot_code, o.plot_no].filter(Boolean).join(' · ') || null,
                      givenOn: o.acknowledged_at, validFrom: o.valid_from, validTo: o.valid_to },
          askedOn: consent!.valid_from, reason: consent!.amendment_reason ?? null,
          changes: (consent!.amendment_changes ?? []).map((change) => ({ key: change.key, label: change.label, was: change.was ?? null, now: change.now })),
        };
      })(),
      consent: { id: consent!.id, language_code: consent!.language_code, valid_from: consent!.valid_from, valid_to: consent!.valid_to, scope_codes: consent!.scope_codes ?? [] },
      organisation: profile?.organization?.name ?? '',
      letterhead: letterhead.data,
      farmer: {
        name: f?.full_name ?? consent!.farmer_name ?? '', code: f?.farmer_code ?? null, fatherOrSpouse: relationName(f?.relation_type, f?.father_or_spouse_name), gender: f?.gender,
        address: f ? (f.communication_address ? `${f.communication_address}${f.pin_code ? ` - ${f.pin_code}` : ''}`
          : [f.village_name, f.block_name, f.district_name, f.state_name].filter(Boolean).join(', ')) : null,
        mobile: party.data?.[0]?.primary_mobile ?? f?.mobile ?? null, email: party.data?.[0]?.primary_email ?? null,
        photoDataUri: await dataUriOf(f?.photo_document_id),
        kyc: (kyc.data ?? []).filter((row) => row.verification_status !== 'rejected')
          .map((row) => ({ type: row.kyc_type_label ?? 'ID', number: row.masked_document_no ?? `on file (${row.verification_status})` })),
      },
      plot: {
        code: p?.plot_code ?? consent!.plot_code ?? '', no: p?.plot_no ?? null, name: p?.plot_name ?? null, village: p?.village_name ?? null,
        address: p ? [p.block_name, p.district_name, p.state_name].filter(Boolean).join(', ') : null,
        declaredArea: area(p?.declared_area_ha ?? null), mappedArea: area(p?.mapped_area_ha ?? null),
        tenure: (tenures.data ?? []).find((row) => row.id === p?.land_tenure_type_id)?.label ?? null,
        landDocuments: landDocs.data ? `${landDocs.data.length} on file` : null,
        species: (species.data ?? []).map((row) => `${row.species_label ?? ''} (${row.survival_count ?? row.tree_count ?? 0})`).join(', ') || null,
        boundary: ring,
        // The boundary on satellite imagery (owner, 2026-09-28: "snapshot of actual polygon").
        boundarySnapshot: await boundarySnapshot(ring),
        excision: (() => {
          const held = (scopes.data ?? []).find((row) => row.excision_declaration);
          if (!held) return null;
          if (held.excision_declaration === 'none') return 'None: no part of this plot is excised from the certified area.';
          const reason = EXCISION_REASONS.find((entry) => entry.id === held.excision_reason)?.label ?? held.excision_reason ?? '';
          return `${held.excised_area_ha ?? '?'} ha excised (${reason})${held.excision_description ? `: ${held.excision_description}` : ''}. Produce from it carries no claim.`;
        })(),
      },
      scopes: scopeRows,
    };
  }
  const partyLevel = !consent?.plot_id && kind !== 'programme';
  const subject = partyLevel ? (f?.farmer_code ?? consent?.farmer_code ?? 'party') : (p?.plot_code ?? consent?.plot_code ?? 'plot');
  const fileBase = `${names.file}-${subject}-${pair}`;
  const local = consentTextFor(lang) ?? consentTextFor('en')!;

  async function store(file: File, linkType: 'generated' | 'primary') {
    const stored = await uploadDocument({ file, bucket: 'generated-documents', organizationId: profile!.organization!.id, ownerId: consent!.party_id, captureMethod: 'generated' });
    await callRpc('fn_link_document', { p_document_id: stored.id, p_entity_table: 'party.party_consents', p_entity_id: consent!.id, p_link_type: linkType });
    await queryClient.invalidateQueries({ queryKey: ['record_documents'] });
    await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
    return stored.id;
  }

  /** The draft to send and read before the OTP, and the print copy to sign by hand: both, always. */
  async function generate() {
    if (!consent || !profile?.organization) return;
    setBusy('generate');
    try {
      const data = await documentData();
      const draftFile = await htmlToPdf(buildPlotConsentHtml(data, 'draft', null, local), `${fileBase}.pdf`);
      await store(draftFile, 'generated');
      await store(await htmlToPdf(buildPlotConsentHtml(data, 'paper', null, local), `${fileBase}-print.pdf`), 'generated');
      setPreview(URL.createObjectURL(draftFile));
      toast.success(lang === 'en' ? 'The draft and the print copy are ready.' : `The draft and the print copy are ready, in English and ${languageName(lang).replace(/^[A-Z]{2} - /, '')}.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The PDF could not be made.');
    } finally {
      setBusy(null);
    }
  }

  /** The print copy, to sign by hand or thumb: the stored one, or a fresh one when there is none yet. */
  async function printPaper() {
    if (!consent) return;
    setBusy('paper');
    try {
      if (printCopy) {
        const url = (await signedUrlsFor([printCopy.document_id]))[printCopy.document_id];
        if (url) window.open(url, '_blank', 'noopener');
        return;
      }
      const file = await htmlToPdf(buildPlotConsentHtml(await documentData(), 'paper', null, local), `${fileBase}-print.pdf`);
      await store(file, 'generated');
      window.open(URL.createObjectURL(file), '_blank', 'noopener');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The PDF could not be made.');
    } finally {
      setBusy(null);
    }
  }


  async function sendDocument(documentIds: string[], stage: 'draft' | 'final') {
    const documentId = documentIds[0];
    if (!documentId) return;
    setBusy(stage === 'draft' ? 'send' : 'final');
    try {
      // The final copy goes with a link to review it online (owner, 2026-09-27).
      let reviewToken: string | null = null;
      if (stage === 'final') {
        try { reviewToken = (await issueConsentReviewLink(consent!.id, 'signed')).token; } catch { reviewToken = null; }
      }
      await sendRecordDocument({ consentId: consent!.id, documentId, documentIds, channels: sendBy, stage, reviewToken });
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
    } finally {
      setBusy(null);
    }
  }

  /** The OTP is verified: the same document, stamped as given, with the draft's fingerprint. */
  async function sign(via: OtpSentVia & { verifiedAt: string; otpEventId: string }) {
    setCapturing(false);
    setBusy('sign');
    try {
      // The signed copy carries the fingerprint of the draft the farmer was sent; placed on the consent
      // at once (owner, 2026-09-28: "once digital consent is obtained, it gets autoplaced").
      const data = await documentData();
      let last: File | null = null;
      {
        const own = latestOf(lang, 'generated');
        let fingerprint: string | null = own?.sha256_hash ?? null;
        if (!fingerprint && own) {
          const url = (await signedUrlsFor([own.document_id]))[own.document_id];
          if (url) fingerprint = await sha256Hex(await (await fetch(url)).arrayBuffer());
        }
        const html = buildPlotConsentHtml(data, 'signed', {
          channels: via.channels.length ? via.channels.map((entry) => ({ channel: entry.channel, destination: entry.destination })) : [{ channel: via.channel, destination: via.destination }],
          sentAt, verifiedAt: via.verifiedAt, verifiedBy: profile?.user.full_name ?? null, reference: via.otpEventId, draftFingerprint: fingerprint,
        }, local);
        last = await htmlToPdf(html, `${fileBase}-signed.pdf`);
        await store(last, 'primary');
      }
      if (last) setPreview(URL.createObjectURL(last));
      toast.success('Consent given. The signed copy is kept on the consent.');
      // Stage 3, on its own: the farmer is told at once that their consent is recorded.
      await sendConfirmationNotice({ consentId: consent!.id });
    } catch (error) {
      toast.error(`Consent is given, but the signed copy was not made: ${error instanceof Error ? error.message : 'error'}.`);
    } finally {
      setBusy(null);
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
    }
  }

  async function withdraw() {
    if (!reason || !f || !consent?.plot_id) { toast.error('Why is the farmer withdrawing this plot?'); return; }
    setBusy('withdraw');
    try {
      const id = await callRpc<string>('fn_request_withdrawal', {
        p_farmer_id: f.id, p_scope: 'plots', p_plot_ids: [consent.plot_id], p_reason_code: reason, p_reason_text: null,
        p_effective_date: todayIso(), p_language_code: consent.language_code,
      });
      const row = (await listRows<{ consent_id: string }>('withdrawals', { filters: { id } }))[0];
      setWithdrawing(false);
      await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      toast.success('Withdrawal recorded. Take the farmer\'s OTP to confirm it.');
      if (row?.consent_id) setWithdrawalConsent(row.consent_id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The withdrawal could not be recorded.');
    } finally {
      setBusy(null);
    }
  }

  // The full record of how consent was given - kept here, not on the document (owner, 2026-09-28:
  // "this detailed info can be added in the app and not in the doc").
  const otpLog = useRows<{ id: string; channel: string; destination_masked: string | null; created_at: string; verified_at: string | null; status: string;
                          also_sent_to: string[] | null }>(
    'otp_log', { filters: { linked_entity_id: consentId, status: 'verified' }, orderBy: { column: 'verified_at', ascending: false } }, consent?.status === 'acknowledged');

  const withdrawalRow = useRows<ConsentRow>('party_consents', { filters: { id: withdrawalConsent ?? undefined } }, Boolean(withdrawalConsent));
  // A withdrawal recorded but not yet confirmed by the farmer's code (found in the 2026-09-28 UI pass:
  // closing the code dialog lost it, and "Withdraw this plot" was offered again).
  const pendingWithdrawals = useRows<{ id: string; withdrawal_code: string | null; consent_id: string | null; plot_ids: string[] | null;
                                       scope: string; requested_on: string }>(
    'withdrawals', { filters: { farmer_id: f?.id ?? undefined, withdrawal_status: 'pending_consent' } }, Boolean(f?.id) && consent?.status === 'acknowledged');
  const pendingWithdrawal = (pendingWithdrawals.data ?? []).find((row) => row.scope === 'membership' || (row.plot_ids ?? []).includes(consent?.plot_id ?? ''));

  if (!consent) return null;
  return (
    <RecordDrawer
      footer={<button onClick={onClose} type="button">Close</button>}
      onClose={onClose} open record={{ table: 'party.party_consents', id: consent.id, invalidate: ['party_consents'] }}
      subtitle={[consent.farmer_name ?? '', kind === 'representative' ? (appointed.data?.[0]?.representative_name ? `appoints ${appointed.data[0].representative_name}` : 'representative')
        : consent.plot_id ? `plot ${[consent.plot_code, consent.plot_no].filter(Boolean).join(' · ')}` : consent.farmer_code, languageName(consent.language_code)].filter(Boolean).join(' · ')}
      title={kind === 'programme' ? `Plot consent - ${(consent.scope_codes ?? []).map(scopeLabel).join(', ') || 'membership'}` : names.title}
      wide
    >
      <dl className="detail-grid spec-view">
        <div><dt>Status</dt><dd><StatusPill value={CONSENT_STATE[consent.status] ?? consent.status} /></dd></div>
        <div><dt>Valid</dt><dd>{consent.valid_to ? <ValidityBadge from={consent.valid_from} to={consent.valid_to} /> : '--'}</dd></div>
        <div><dt>PDF sent</dt><dd>{consent.document_sent_at ? `${formatDateTime(consent.document_sent_at)} by ${(consent.document_sent_via ?? []).join(', ')}` : 'Not yet'}</dd></div>
        <div><dt>Given</dt><dd>{consent.acknowledged_at ? `${formatDateTime(consent.acknowledged_at)} (${consent.acknowledgement_method?.replace(/_/g, ' ') ?? ''})` : 'Not yet'}</dd></div>
        {consent.amends_consent_id && (
          <div className="spec-view-wide"><dt>Amends</dt><dd>
            {original.data?.[0] ? `${PLOT_CONSENT_NAMES[plotConsentKind(original.data[0].consent_type)].title}, given ${original.data[0].acknowledged_at ? formatDate(original.data[0].acknowledged_at) : '--'}` : 'the signed consent'}
            {' '}<button className="link-button" onClick={() => navigate(`/plot-consents?consent=${consent.amends_consent_id}`)} type="button">Open it</button>
            {' · '}{(consent.amendment_changes ?? []).map((change) => `${CHANGE_ITEMS.find((item) => item.key === change.key)?.label ?? change.key}: ${change.now}`).join('; ')}
            {consent.amendment_reason ? ` · ${consent.amendment_reason}` : ''}
          </dd></div>
        )}
      </dl>

      <section className="consent-steps">
        <h3><FileText size={16} /> 1. The documents</h3>
        <p className="muted">{kind === 'programme' ? 'Farmer, plot, and one page of obligations for each standard.'
          : kind === 'sale' ? 'Farmer, plot, and what the farmer agrees to for harvest and sale.'
          : kind === 'representative' ? 'Farmer, the representative appointed, the matters they may act in and the plots; the farmer and the representative both sign the print copy.'
          : kind === 'withdrawal' ? 'Farmer, and what is withdrawn, why and from when.'
          : kind === 'amendment' ? 'Farmer, the consent this amends, and a table of what changes against what was; everything else stays as signed.'
          : kind === 'dataProcessing' ? 'Farmer, the information kept and how it is used and shared, and their rights.'
          : partyLevel ? 'Farmer, what this consent covers, what they agree to and what they are entitled to.'
          : `Farmer, plot, and the ${kind === 'family' ? 'family members' : 'landowner and sharecropper'} who consent with them (${parties.length ? parties.map((row) => row.name).join(', ') : `none recorded yet: add them on the plot's ${kind === 'family' ? 'Family members' : 'Sharecropping'} tab, or they write their names on the print copy`}).`}
          {' '}One document in English{lang === 'en' ? '' : ` and ${languageName(lang).replace(/^[A-Z]{2} - /, '')}`}, stacked; generating makes the draft
          (for the digital consent) and the print copy (to sign by hand) together.</p>
        <div className="inline-form-actions">
          <button disabled={Boolean(busy) || !waiting} onClick={() => void generate()} type="button">{busy === 'generate' ? 'Making the PDFs...' : draft ? 'Generate again' : 'Generate draft and print copy'}</button>
          <button disabled={Boolean(busy)} onClick={() => void printPaper()} type="button"><Printer size={14} /> {busy === 'paper' ? 'Making the PDF...' : printCopy ? 'Open the print copy' : 'Make the print copy'}</button>
        </div>
        {preview && <iframe className="consent-preview" src={preview} title="Consent PDF" />}

        <h3><ShieldCheck size={16} /> 2. Digital consent (OTP)</h3>
        <h4 className="consent-step-sub"><Send size={14} /> Send draft</h4>
        <p className="muted">As a PDF by WhatsApp or email; an SMS (no link) tells the farmer it was sent. A farmer with neither gets it printed.</p>
        <ul className="toggle-list">
          {docChannels.map((row) => {
            const on = sendBy.includes(row.channel as 'whatsapp' | 'email');
            return (
              <li key={row.channel}>
                <span>{row.channel === 'email' ? <Mail size={14} /> : <MessageCircle size={14} />} {row.channel === 'email' ? 'Email' : 'WhatsApp'} {row.available ? row.destination_masked : <small className="muted">- not recorded</small>}</span>
                <button aria-pressed={on} className="onoff-toggle" disabled={!row.available} type="button"
                        onClick={() => setSendBy(on ? sendBy.filter((c) => c !== row.channel) : [...sendBy, row.channel as 'whatsapp' | 'email'])}>{on ? 'On' : 'Off'}</button>
              </li>
            );
          })}
        </ul>
        <div className="inline-form-actions">
          <button disabled={!draft || Boolean(busy) || !waiting} onClick={() => void sendDocument(drafts.map((row) => row.document_id), 'draft')} type="button">
            <Send size={14} /> {busy === 'send' ? 'Sending...' : 'Send draft'}
          </button>
        </div>

        <h4 className="consent-step-sub"><ShieldCheck size={14} /> Send OTP for consent</h4>
        <p className="muted">The code goes to the farmer's own number (SMS / WhatsApp) or email and names the organisation, the consent and the plot.
          Once they read it back they are sent a message at once that their consent is recorded.</p>
        <div className="inline-form-actions">
          <button className="primary" disabled={!draft || !waiting || !mayCapture || Boolean(busy)} onClick={() => { setSentAt(new Date().toISOString()); setCapturing(true); }} type="button">
            <ShieldCheck size={14} /> {busy === 'sign' ? 'Making the signed copy...' : 'Send OTP for consent'}
          </button>
        </div>

        <h4 className="consent-step-sub"><Send size={14} /> Send the consented copy</h4>
        <p className="muted">The signed PDF, stamped with how and when the consent was given, by WhatsApp or email, with a private link to review it
          online for 30 days; an SMS (no link) says it was sent.</p>
        <div className="inline-form-actions">
          <button className="primary" disabled={!signed || Boolean(busy)} onClick={() => void sendDocument(signedCopies.map((row) => row.document_id), 'final')} type="button">
            <Send size={14} /> {busy === 'final' ? 'Sending...' : 'Send final consented copies'}
          </button>
          {signed && (
            <button onClick={() => void issueConsentReviewLink(consent.id, 'signed')
                      .then(({ url }) => navigator.clipboard.writeText(url).then(() => toast.success('Review link copied: valid 30 days.'), () => toast.info(url)))
                      .catch((error: unknown) => toast.error(error instanceof Error ? error.message : 'The link could not be made.'))}
                    type="button">
              <Link2 size={14} /> Copy review link
            </button>
          )}
        </div>
        {!signed && <p className="muted">Available once the consent is given.</p>}
        {!draft && waiting && <p className="muted">Generate the documents first: the farmer is asked to agree to what they were sent.</p>}

        <h3><Printer size={16} /> 3. Print consent (signed paper)</h3>
        <p className="muted">For a farmer who signs by hand or thumb{kind === 'family' || kind === 'sharecrop' ? ', and for everyone named with them, who sign the same copy' : ''}:
          print the print copy, sign every page before a witness, then drop the signed scan on the Documents row below and record it.</p>
        <div className="inline-form-actions">
          <button disabled={Boolean(busy)} onClick={() => void printPaper()} type="button"><Printer size={14} /> {printCopy ? 'Open the print copy' : 'Make the print copy'}</button>
          <button disabled={!waiting || !mayCapture} onClick={() => setPaper(true)} type="button">Record signed paper</button>
        </div>

        {consent.status === 'acknowledged' && otpLog.data?.[0] && (
          <>
            <h3><ShieldCheck size={16} /> Consent record</h3>
            <dl className="detail-grid spec-view">
              <div><dt>Code sent</dt><dd>{formatDateTime(otpLog.data[0].created_at)}</dd></div>
              <div><dt>Verified</dt><dd>{otpLog.data[0].verified_at ? formatDateTime(otpLog.data[0].verified_at) : '--'}</dd></div>
              {/* Every channel the one code went to: the first, and those ticked with it ("sms:***0001"). */}
              <div><dt>Sent to</dt><dd>{[`${otpLog.data[0].channel}:${otpLog.data[0].destination_masked ?? ''}`, ...(otpLog.data[0].also_sent_to ?? [])]
                .map((entry) => { const [channel, ...rest] = entry.split(':'); return `${channel === 'sms' ? 'SMS' : channel === 'whatsapp' ? 'WhatsApp' : 'Email'} ${rest.join(':')}`; })
                .join(' · ')}</dd></div>
              <div><dt>Method</dt><dd>{consent.acknowledgement_method?.replace(/_/g, ' ') ?? '--'}</dd></div>
              <div className="spec-view-wide"><dt>OTP reference and fingerprints</dt><dd>In Documents below, masked; your own OTP reveals them.</dd></div>
            </dl>
          </>
        )}

        {consent.status === 'acknowledged' && !['withdrawal', 'consent_amendment'].includes(consent.consent_type) && mayIssue && (
          <>
            <h3><FilePen size={16} /> Amend this consent</h3>
            {(amendments.data ?? []).length > 0 && (
              <div className="line-table-wrap">
                <table className="simple-table">
                  <thead><tr><th>Amendment</th><th>What changes</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {(amendments.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td>No. {row.amendment_no ?? '--'} · {formatDate(row.valid_from)}</td>
                        <td>{(row.amendment_changes ?? []).map((change) => `${CHANGE_ITEMS.find((item) => item.key === change.key)?.label ?? change.key}: ${change.now}`).join('; ')}</td>
                        <td><StatusPill value={CONSENT_STATE[row.status] ?? row.status} /></td>
                        <td><button className="link-button" onClick={() => navigate(`/plot-consents?consent=${row.id}`)} type="button">Open</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(amendments.data ?? []).some((row) => ['pending', 'generated'].includes(row.status)) ? (
              <p className="muted">An amendment waits for the farmer; finish or withdraw it before starting another.</p>
            ) : amending ? (
              <AmendConsent address={f ? (f.communication_address ?? [f.village_name, f.block_name, f.district_name].filter(Boolean).join(', ')) : null}
                            consent={consent} email={party.data?.[0]?.primary_email ?? null} mobile={party.data?.[0]?.primary_mobile ?? f?.mobile ?? null}
                            onClose={() => setAmending(false)}
                            onCreated={(id) => { setAmending(false); navigate(`/plot-consents?consent=${id}`); }} />
            ) : (
              <p className="muted">A given consent is not edited. What changes is written as an amendment the farmer signs the same way; once signed,
                a new end date or programmes are applied to this consent. <button className="link-button" onClick={() => setAmending(true)} type="button">Amend this consent</button></p>
            )}
          </>
        )}

        {consent.status === 'acknowledged' && consent.plot_id && kind !== 'amendment' && (
          <>
            <h3><Undo2 size={16} /> Withdraw this plot</h3>
            {pendingWithdrawal ? (
              <p className="muted">
                Withdrawal {pendingWithdrawal.withdrawal_code ?? ''} was recorded on {formatDate(pendingWithdrawal.requested_on)} and waits for the farmer&apos;s OTP.{' '}
                {pendingWithdrawal.consent_id && (
                  <button className="link-button" onClick={() => setWithdrawalConsent(pendingWithdrawal.consent_id)} type="button">Take the farmer&apos;s OTP</button>
                )}
              </p>
            ) : withdrawing ? (
              <div className="field-row">
                <Field label="Reason" required><Select onChange={setReason} options={WITHDRAWAL_REASONS} value={reason} /></Field>
                <div className="inline-form-actions">
                  <button className="cancel" onClick={() => setWithdrawing(false)} type="button">Cancel</button>
                  <button disabled={busy === 'withdraw'} onClick={() => void withdraw()} type="button">Record and take OTP</button>
                </div>
              </div>
            ) : (
              <p className="muted">The farmer may withdraw at any time; it is confirmed by their OTP too. <button className="link-button" onClick={() => setWithdrawing(true)} type="button">Withdraw this plot</button></p>
            )}
          </>
        )}

        <h3>Documents</h3>
        <ConsentCopies consentId={consent.id} printCopy={printCopy ?? null} documents={documents.data ?? []} given={consent.status === 'acknowledged'}
                       languages={languages} mayUpload={mayCapture && !signed} otpReference={otpLog.data?.[0]?.id ?? null}
                       filePrefix={names.file} plotCode={subject} />
      </section>

      {capturing && <ConsentCapture consent={consent} onClose={() => setCapturing(false)} onDone={(via) => void sign(via)} />}
      {paper && (
        <PaperConsent consentId={consent.id} existingSignedId={signed && /-paper\./.test(signed.original_filename ?? '') ? signed.document_id : null}
                      fileBase={fileBase} onClose={() => setPaper(false)} onOpenPrintCopy={() => void printPaper()}
                      onRecorded={() => { setPaper(false); void queryClient.invalidateQueries({ queryKey: ['party_consents'] }); }} partyId={consent.party_id} />
      )}
      {withdrawalRow.data?.[0] && (
        <ConsentCapture consent={withdrawalRow.data[0]} onClose={() => setWithdrawalConsent(null)}
                        onDone={() => {
                          const id = withdrawalConsent;
                          setWithdrawalConsent(null);
                          toast.success('The plot is withdrawn.');
                          if (id) void sendConfirmationNotice({ consentId: id });
                          void queryClient.invalidateQueries({ queryKey: ['party_consents'] });
                        }} />
      )}
    </RecordDrawer>
  );
}

/** The items an amendment may change: the same keys the database checks (party.fn_check_amendment_changes). */
const CHANGE_ITEMS: Array<{ key: string; label: string }> = [
  { key: 'mobile', label: 'Mobile' }, { key: 'email', label: 'Email' }, { key: 'address', label: 'Address' },
  { key: 'valid_to', label: 'Valid to' }, { key: 'scope_codes', label: 'Programmes this consent covers' },
  { key: 'rights_holder', label: 'Rights holder named' }, { key: 'other', label: 'Other' },
];

interface Change { key: string; was: string; now: string; codes: string[] }

/**
 * What changes on a signed consent, item by item against what the record holds now, and why. It
 * becomes a consent_amendment row the farmer signs like any consent (owner, 2026-10-08).
 */
function AmendConsent({ consent, mobile, email, address, onClose, onCreated }: {
  consent: ConsentRow; mobile: string | null; email: string | null; address: string | null; onClose: () => void; onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [changes, setChanges] = useState<Change[]>([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const wasOf = (key: string) => key === 'mobile' ? mobile ?? '' : key === 'email' ? email ?? '' : key === 'address' ? address ?? ''
    : key === 'valid_to' ? consent.valid_to ?? '' : key === 'scope_codes' ? (consent.scope_codes ?? []).map(scopeLabel).join(', ') : '';
  const add = (key: string) => { if (key) setChanges((now) => [...now, { key, was: wasOf(key), now: '', codes: key === 'scope_codes' ? [...(consent.scope_codes ?? [])] : [] }]); };
  const update = (index: number, patch: Partial<Change>) => setChanges((now) => now.map((change, at) => (at === index ? { ...change, ...patch } : change)));
  const left = CHANGE_ITEMS.filter((item) => item.key === 'other' || !changes.some((change) => change.key === item.key));
  const nowOf = (change: Change) => (change.key === 'scope_codes' ? change.codes.map(scopeLabel).join(', ') : change.now.trim());

  async function create() {
    if (changes.length === 0) { toast.error('Add at least one change.'); return; }
    const blank = changes.find((change) => !nowOf(change));
    if (blank) { toast.error(`Say what ${CHANGE_ITEMS.find((item) => item.key === blank.key)?.label.toLowerCase() ?? blank.key} is now.`); return; }
    setSaving(true);
    try {
      const id = await callRpc<string>('fn_issue_consent_amendment', {
        p_consent_id: consent.id, p_reason: reason.trim() || null,
        p_changes: changes.map((change) => ({
          key: change.key, label: CHANGE_ITEMS.find((item) => item.key === change.key)?.label ?? change.key, was: change.was || null, now: nowOf(change),
          ...(change.key === 'scope_codes' ? { codes: change.codes } : {}),
        })),
      });
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
      toast.success('Amendment created. Generate its document next, then take the farmer\'s OTP.');
      onCreated(id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The amendment could not be created.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="field-row">
      {changes.map((change, index) => {
        const item = CHANGE_ITEMS.find((entry) => entry.key === change.key)!;
        return (
          <Field hint={change.was ? `Now on record: ${change.was}` : 'Nothing on record yet.'} key={`${change.key}-${index}`} label={item.label} required>
            {change.key === 'valid_to' ? (
              <input min={todayIso()} onChange={(event) => update(index, { now: event.target.value })} type="date" value={change.now} />
            ) : change.key === 'scope_codes' ? (
              <ul className="toggle-list">
                {PLOT_SCOPES.map((scope) => {
                  const on = change.codes.includes(scope.code);
                  return (
                    <li key={scope.code}><span>{scope.label}</span>
                      <button aria-pressed={on} className="onoff-toggle" type="button"
                              onClick={() => update(index, { codes: on ? change.codes.filter((code) => code !== scope.code) : [...change.codes, scope.code] })}>{on ? 'On' : 'Off'}</button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <input onChange={(event) => update(index, { now: event.target.value })} placeholder={change.key === 'other' ? 'What changes, in a sentence' : `${item.label} now`} value={change.now} />
            )}
            <button className="link-button" onClick={() => setChanges((now) => now.filter((_entry, at) => at !== index))} type="button">Remove</button>
          </Field>
        );
      })}
      <Field hint="Each item is shown against what the record holds now." label="Add a change">
        <Select onChange={add} options={left.map((item) => ({ id: item.key, label: item.label }))} placeholder="Choose an item" value="" />
      </Field>
      <Field hint="In the office's words; it is printed on the amendment." label="Reason">
        <input onChange={(event) => setReason(event.target.value)} placeholder="Why the farmer asked for it" value={reason} />
      </Field>
      <div className="inline-form-actions">
        <button className="cancel" onClick={onClose} type="button">Cancel</button>
        <button className="primary" disabled={saving || changes.length === 0} onClick={() => void create()} type="button">{saving ? 'Creating...' : 'Create the amendment'}</button>
      </div>
    </div>
  );
}
